import {prop} from 'ramda';

import {DASHBOARD_TILE_SIZES} from '../consts/dashboardTileSizes';
import {DashboardTileCoordinates} from '../types/DashboardTileCoordinates';
import {DashboardTileId} from '../types/DashboardTileId';
import {DashboardTileSize} from '../types/DashboardTileSize';

// One column is a quarter of the board; every tile width divides it.
export const DASHBOARD_GRID_COLUMNS = 4;
export const DASHBOARD_MAX_ROW = 63;

export interface DashboardGridTile {
  id: DashboardTileId;
  size: DashboardTileSize;
}

export interface PositionedDashboardGridTile extends DashboardGridTile, DashboardTileCoordinates {}

interface AllocateDashboardGridOptions {
  occupied?: PositionedDashboardGridTile[];
  startRow?: number;
}

const getBottom = (tile: PositionedDashboardGridTile) =>
  tile.row + prop(tile.size, DASHBOARD_TILE_SIZES).rows;

export const dashboardGridTilesOverlap = (
  first: PositionedDashboardGridTile,
  second: PositionedDashboardGridTile
) => {
  const firstSize = prop(first.size, DASHBOARD_TILE_SIZES);
  const secondSize = prop(second.size, DASHBOARD_TILE_SIZES);

  return (
    first.column < second.column + secondSize.columns &&
    first.column + firstSize.columns > second.column &&
    first.row < second.row + secondSize.rows &&
    first.row + firstSize.rows > second.row
  );
};

// A tile starts only at a multiple of its own width, so halves never straddle the board middle.
export const isDashboardGridPositionValid = (
  tile: Pick<PositionedDashboardGridTile, 'size' | 'column' | 'row'>
) => {
  const size = prop(tile.size, DASHBOARD_TILE_SIZES);

  return (
    Number.isInteger(tile.column) &&
    Number.isInteger(tile.row) &&
    tile.column >= 0 &&
    tile.row >= 0 &&
    tile.row <= DASHBOARD_MAX_ROW &&
    tile.column + size.columns <= DASHBOARD_GRID_COLUMNS &&
    tile.column % size.columns === 0
  );
};

const findFirstPosition = (
  tile: DashboardGridTile,
  occupied: PositionedDashboardGridTile[],
  startRow: number,
  lastRow: number
): DashboardTileCoordinates | null => {
  const width = prop(tile.size, DASHBOARD_TILE_SIZES).columns;

  for (let row = startRow; row <= lastRow; row += 1) {
    for (let column = 0; column <= DASHBOARD_GRID_COLUMNS - width; column += width) {
      const candidate = {...tile, column, row};

      if (!occupied.some((placed) => dashboardGridTilesOverlap(candidate, placed))) {
        return {column, row};
      }
    }
  }

  return null;
};

export function allocateDashboardGrid(
  tiles: DashboardGridTile[],
  options: AllocateDashboardGridOptions = {}
): PositionedDashboardGridTile[] | null {
  const startRow = options.startRow ?? 0;
  const placed = [...(options.occupied ?? [])];
  const occupiedBottom = placed.reduce(
    (bottom, tile) => Math.max(bottom, getBottom(tile)),
    startRow
  );
  const queuedRows = tiles.reduce(
    (rows, tile) => rows + prop(tile.size, DASHBOARD_TILE_SIZES).rows,
    0
  );
  const lastRow = Math.min(DASHBOARD_MAX_ROW, Math.max(startRow, occupiedBottom) + queuedRows);
  const allocated: PositionedDashboardGridTile[] = [];

  for (const tile of tiles) {
    const position = findFirstPosition(tile, placed, startRow, lastRow);

    if (position === null) {
      return null;
    }

    const positioned = {id: tile.id, size: tile.size, ...position};
    placed.push(positioned);
    allocated.push(positioned);
  }

  return allocated;
}

export const compareDashboardGridPositions = (
  first: DashboardTileCoordinates,
  second: DashboardTileCoordinates
) => first.row - second.row || first.column - second.column;
